import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import { useModalManager } from '../../../../hooks/ModalManager.hook';
import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';

import {
  createRegionControlThunk,
  deleteRegionControlThunk,
  getRegionControlListThunk,
  updateRegionControlThunk,
} from '../../../../redux/ControlPanel/RegionControl/RegionControl.thunk';

import CardComponent from '../../../../components/Card.component';
import ButtonComponent from '../../../../components/Button.component';
import InputComponent from '../../../../components/Input/Input.component';
import SpinnerComponent from '../../../../components/Spinner.component';
import ControlCreateEditModal from '../../../../components/Modal/Control/ControlCreateEditModal';
import ConfirmDuplicateModal from '../../../../components/Modal/Control/ConfirmDuplicateModal';
import ControlSectionContent from './ControlSectionContent.component';
import { SortTypes } from '../../../../types/sort.types';

import SearchIcon from '../../../../components/Icons/Search.icon';
import SortArrowsIcon from '../../../../components/Icons/SortArrows.icon';

import {
  StyledControlListItem,
  StyledControlSectionContainer,
  StyledControlSectionHeader,
  StyledHeaderButtonsWrapper,
  StyledHeaderLabel,
  StyledHeaderTitle,
  StyledSearchIcon,
  StyledSearchWrapper,
  StyledSortContainer,
} from './sharedStyledComponents';
import { checkUserRequiredPermissions } from '../../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../../types/userPermissions.types';
import useAuthHook from '../../../../hooks/useAuth.hook';
import { ControlSectionTypes } from '../../../../redux/ControlPanel/ControlPanelList/ControlPanelList.type';
import { useSelector } from 'react-redux';
import {
  getRegionControlList,
  getRegionControlListPagination,
  setRegionControlList,
} from '../../../../redux/ControlPanel/RegionControl/RegionControl.slice';
import { Region } from '../../../../redux/Region/Region.type';

const sectionType = ControlSectionTypes.REGIONS;

export const RegionsSectionComponent: FC = () => {
  const { user } = useAuthHook();
  const { openModal } = useModalManager();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [sort, setSort] = useState(SortTypes.ASC);

  const regions = useSelector(getRegionControlList);
  const pagination = useSelector(getRegionControlListPagination);

  const editable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_EDIT_REGION,
  ]);

  const creatable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_CREATE_REGION,
  ]);

  const deletable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_DELETE_REGION,
  ]);

  const viewable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_VIEW_REGION,
  ]);

  const fetchParams = useMemo(() => {
    return {
      q: searchQuery,
      sort: `${sort === SortTypes.DESC ? '-' : ''}name`,
    };
  }, [sort, searchQuery]);

  const createItemWithModalWrapper = async (
    fetchFunc: Function,
    updateFunc: Function,
    payload: any,
    updateFetchParams?: any,
    message?: string,
  ) => {
    dispatch(fetchFunc(payload)).then((res: any) => {
      if (!res.payload && !!res.error) {
        openModal(ConfirmDuplicateModal, {
          message: message ?? t(`Such a record already exists!`),
        });
      } else {
        dispatch(updateFunc(updateFetchParams));
      }
    });
  };

  const updateListAfterAction = () => {
    dispatch(getRegionControlListThunk(fetchParams));
  };

  const createNewSectionItem = (newItem: Region) => {
    createItemWithModalWrapper(
      createRegionControlThunk,
      getRegionControlListThunk,
      {
        name: newItem.name,
        code: newItem.name?.toLocaleLowerCase(),
      },
      fetchParams,
      t('Such a region already exists!'),
    );
  };

  const editSectionItem = (sectionItem: Region) => {
    createItemWithModalWrapper(
      updateRegionControlThunk,
      getRegionControlListThunk,
      sectionItem,
      fetchParams,
    );
  };

  const deleteSectionItem = (sectionItem: Region) => {
    dispatch(deleteRegionControlThunk(sectionItem.id)).then(() =>
      updateListAfterAction(),
    );
  };

  const handleSortChange = (sortOrder: SortTypes) => {
    handleSort(sortOrder);

    dispatch(
      getRegionControlListThunk({
        page: 1,
        q: searchQuery,
        sort: `${sortOrder === SortTypes.ASC ? '-' : ''}name`,
      }),
    );
  };

  const handleSearch = (value: string) => {
    setLoading(true);

    dispatch(
      getRegionControlListThunk({
        q: value,
        page: 1,
        sort: `${sort === SortTypes.DESC ? '-' : ''}name`,
      }),
    ).finally(() => setLoading(false));
  };

  const debounceSearchValue = useCallback(
    debounce(handleSearch, 300) as Function,
    [],
  );

  const handleSort = (sort: SortTypes) => {
    setSort(sort === SortTypes.ASC ? SortTypes.DESC : SortTypes.ASC);
  };

  const updateItems = (items: Region[]) => {
    dispatch(setRegionControlList(items));
  };

  useEffect(() => {
    dispatch(getRegionControlListThunk(fetchParams));
  }, []);

  if (!viewable) return null;

  return (
    <CardComponent>
      <StyledControlSectionContainer>
        <StyledControlSectionHeader>
          <StyledHeaderTitle>
            <StyledHeaderLabel>{t('Region management')}</StyledHeaderLabel>

            <StyledSortContainer onClick={() => handleSortChange(sort)}>
              <SortArrowsIcon sortOrder={sort} svgId={sectionType} />
            </StyledSortContainer>
          </StyledHeaderTitle>

          <StyledHeaderButtonsWrapper>
            {creatable && (
              <ButtonComponent
                handleClick={() =>
                  openModal(ControlCreateEditModal, {
                    sectionType,
                    createNewSectionItem,
                    editable,
                    deletable,
                  })
                }
                textColor="#588fff"
                customColor="#ebf0ff"
              >
                {t('Create')}
              </ButtonComponent>
            )}
          </StyledHeaderButtonsWrapper>
        </StyledControlSectionHeader>

        <StyledSearchWrapper>
          <InputComponent
            value={searchQuery}
            onChange={(searchValue: string) => {
              setSearchQuery(searchValue);
              debounceSearchValue(searchValue);
            }}
            onEnter={handleSearch}
            placeholder={t(`Search ${sectionType}`)}
            right={
              <StyledSearchIcon>
                {isLoading ? <SpinnerComponent size="small" /> : <SearchIcon />}
              </StyledSearchIcon>
            }
          />
        </StyledSearchWrapper>
        <ControlSectionContent
          isLoadMode={!(pagination?.currentPage === pagination?.lastPage)}
          initialData={regions}
          fetchData={getRegionControlListThunk}
          sectionType={sectionType}
          fetchParams={{
            q: searchQuery,
            sort: `${sort === SortTypes.DESC ? '-' : ''}name`,
            page: pagination?.currentPage || 1,
          }}
          updateItems={updateItems}
          renderItem={item => (
            <StyledControlListItem
              key={item.id}
              highlighted={item.isAllRegions}
              onClick={() =>
                openModal(ControlCreateEditModal, {
                  sectionType,
                  sectionItem: item,
                  editSectionItem,
                  deleteSectionItem,
                  editable,
                  deletable,
                })
              }
            >
              {item.truncatedDisplayName || item.name}
            </StyledControlListItem>
          )}
        />
      </StyledControlSectionContainer>
    </CardComponent>
  );
};